import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { fetchMessages, listConversations, sendMessage, subscribeToMessages, fetchProfilesByIds } from "../services/chat";
import { supabase } from "../supabase";

export default function useChat() {
    const { user } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const unsubscribeRef = useRef(null);
    const [profiles, setProfiles] = useState({}); // profileId -> profile
    const preferredConversationIdRef = useRef(null); // navigation target to prefer selecting

    const userId = user?.id;

    const loadConversations = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const data = await listConversations(userId);
            setConversations(data);
            // fetch participant profiles for display names
            const participantIds = data.flatMap((c) => [c.participant_a, c.participant_b]);
            const profs = await fetchProfilesByIds(participantIds);

            // Enrich ALL profiles missing companyname with fallback from internal_job_listings
            // This handles cases where role is wrong in users_profiles
            const allProfileIds = profs.map(p => p.profileid);
            
            if (allProfileIds.length > 0) {
                const { data: jobs, error: jobsError } = await supabase
                    .from('internal_job_listings')
                    .select('employer_id, company_name, created_at')
                    .in('employer_id', allProfileIds)
                    .eq('status', 'published')
                    .order('created_at', { ascending: false });

                if (!jobsError && jobs && jobs.length > 0) {
                    const companyByEmployer = {};
                    for (const job of jobs) {
                        if (!companyByEmployer[job.employer_id] && job.company_name) {
                            companyByEmployer[job.employer_id] = job.company_name;
                        }
                    }
                    
                    // CRITICAL: Do NOT modify role in memory - this can cause role switching bugs
                    // Only enrich companyname for display, but preserve original role from database
                    // If role is wrong in DB, that's a data issue that should be fixed at the source
                    for (const p of profs) {
                        if (companyByEmployer[p.profileid]) {
                            // Only add companyname for display - DO NOT change role
                            // The role should come from users_profiles table, not inferred from job listings
                            p.companyname = companyByEmployer[p.profileid];
                            // DO NOT modify p.role here - it must match the database value
                            // If someone has job listings but role is Employee, that's a data integrity issue
                            // that should be investigated, not "fixed" by changing role in memory
                        }
                    }
                }
            }

            const map = {};
            for (const p of profs) {
                map[p.profileid] = p;
            }
            setProfiles((prev) => ({ ...prev, ...map }));

            // Only auto-select if navigated via URL/state (preferred conversation)
            const preferredId = preferredConversationIdRef.current;
            const preferredExists = preferredId && data.some(c => c.id === preferredId);

            if (preferredExists) {
                setActiveConversationId(preferredId);
                // Clear the preference after using it once
                preferredConversationIdRef.current = null;
            } else if (data.length > 0 && !activeConversationId) {
                // Auto-select the most recent conversation (first in list, ordered by last_message_at DESC)
                // This defaults to the conversation with the most recent message
                setActiveConversationId(data[0].id);
            }
        } catch (error) {
            // Error loading conversations or profiles
        } finally {
            setLoading(false);
        }
    }, [userId, activeConversationId]);

    const loadMessages = useCallback(async (conversationId) => {
        if (!conversationId) return;
        setLoading(true);
        try {
            const data = await fetchMessages(conversationId);
            setMessages(data);
        } finally {
            setLoading(false);
        }
    }, []);

    const selectConversation = useCallback((conversationId) => {
        setActiveConversationId(conversationId);
    }, []);

    const preferConversation = useCallback((conversationId) => {
        preferredConversationIdRef.current = conversationId;
    }, []);

    const send = useCallback(async (content, attachments = null) => {
        if (!activeConversationId || !userId) return;
        // Allow sending messages with only attachments (no text content)
        if (!content?.trim() && (!attachments || attachments.length === 0)) return;
        
        // Optimistically add the message to the UI immediately
        const tempMessage = {
            id: `temp-${Date.now()}`,
            conversation_id: activeConversationId,
            sender_id: userId,
            content: content?.trim() || '',
            created_at: new Date().toISOString(),
            attachments: attachments && Array.isArray(attachments) && attachments.length > 0 ? attachments : null
        };
        
        setMessages(prev => [...prev, tempMessage]);
        
        try {
            const sentMessage = await sendMessage(activeConversationId, userId, content?.trim() || '', attachments);
            // Replace the temp message with the real one
            setMessages(prev => prev.map(msg => 
                msg.id === tempMessage.id ? sentMessage : msg
            ));
            // Reload conversations to update ordering (last_message_at should be updated by DB trigger)
            // This ensures the conversation list shows the most recent conversation first
            setTimeout(() => {
                loadConversations();
            }, 500);
        } catch (error) {
            console.error('[useChat] Error sending message:', error);
            // Remove the temp message if sending failed
            setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
            throw error;
        }
    }, [activeConversationId, userId]);

    // load conversations on mount/user change
    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    // load messages and subscribe when activeConversationId changes
    useEffect(() => {
        if (!activeConversationId) return;

        loadMessages(activeConversationId);

        // subscribe to new messages
        unsubscribeRef.current?.();
        unsubscribeRef.current = subscribeToMessages(activeConversationId, (newMessage) => {
            setMessages((prev) => {
                // Avoid duplicates by checking if message already exists
                const exists = prev.some(msg => msg.id === newMessage.id);
                if (exists) return prev;
                return [...prev, newMessage];
            });
            // Reload conversations when a new message arrives to update ordering
            // This ensures conversations are sorted by most recent message
            setTimeout(() => {
                loadConversations();
            }, 500);
        });

        return () => {
            unsubscribeRef.current?.();
            unsubscribeRef.current = null;
        };
    }, [activeConversationId, loadMessages]);

    useEffect(() => {
        if (!userId || !conversations?.length) return;
        const participantIds = new Set();
        conversations.forEach((c) => {
            if (c?.participant_a) participantIds.add(c.participant_a);
            if (c?.participant_b) participantIds.add(c.participant_b);
        });
        const missingProfileIds = Array.from(participantIds).filter((id) => id && !profiles[id]);
        if (missingProfileIds.length === 0) return;

        let isActive = true;
        fetchProfilesByIds(missingProfileIds)
            .then((profs) => {
                if (!isActive || !profs?.length) return;
                const nextMap = {};
                for (const p of profs) {
                    nextMap[p.profileid] = p;
                }
                setProfiles((prev) => ({ ...prev, ...nextMap }));
            })
            .catch((error) => {
                // Failed to hydrate missing profiles
            });

        return () => {
            isActive = false;
        };
    }, [userId, conversations, profiles]);

    const state = useMemo(() => ({
        conversations,
        activeConversationId,
        messages,
        loading,
        profiles,
    }), [conversations, activeConversationId, messages, loading, profiles]);

    const api = useMemo(() => ({
        selectConversation,
        send,
        reload: loadConversations,
        preferConversation,
    }), [selectConversation, send, loadConversations, preferConversation]);

    return { ...state, ...api };
}


